<template>
  <LoadingIndicator 
    v-if="loading"
    topic="Project"
  />
  <div v-else>
    <ProjectMetricsSection
      v-if="projectSummary"
      :projectSummary="projectSummary"
      :projectMetrics="projectMetrics"
    />
    <b-row class="widget-row">
      <b-col cols="12" class="widget-col">
        <SurveyResults
          :periods="periods"
          :selectedPeriod="periodName"
          :chartData="byQuestionData"
          :comments="comments"
          :projectId="projectId"
          :allProjectUsers="allProjectUsers"
          :presetTags="presetTags"
          @refresh="refreshProjectItems"
          @update="updateByQuestion"
          @hide="hideComment"
          @unhide="unhideComment"
        />
      </b-col>
    </b-row>
    <b-row class="widget-row">
      <b-col cols="12" class="widget-col">
        <ProjectMetricListNew
          :projectId="projectId"
          :projectMetrics="projectMetrics"
          :projectUsers="projectUsers"
          :possibleAssignees="projectUsers"
          :documents="documents"
          :meetings="meetings"
          :news="news"
          :presetTags="presetTags"
          @refresh="refreshProjectItems"
          @newMeeting="addMeeting"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import ProjectMetricsSection from "@/components/project/ProjectMetricsSection.vue";
import SurveyResults from "@/components/survey/SurveyResults.vue";
import ProjectMetricListNew from "@/components/project/ProjectMetricListNew.vue";

export default {
  components: {
    ProjectMetricsSection,
    SurveyResults,
    ProjectMetricListNew
  },
  props: {
    projectSummary: { type: Object, required: true }
  },
  data() {
    return {
      loading: false,
      project: null,
      projectId: '',
      surveyId: '',
      projectCost: 0,
      projectDays: null,
      periodName: '',
      periods: [],
      periodDate: null,
      byQuestionData: [],
      comments: [],
      projectUsers: [],
      projectMetrics: [],
      documents: [],
      news: [],
      presetTags: [],
      meetings: [],
      facilitatorSurveys: [],
      allProjectUsers: [],
      errors: [],
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'getDataLoading',
      hasLoaded: 'getDataLoaded',
    }),
  },
  watch: {
    projectSummary() {
      this.setupProjectDetails();
    }
  },
  mounted() {
    // this.loadData();
    this.setupProjectDetails();
  },
  methods: {
    setupProjectDetails() {
      this.loading = true;
      this.project = this.projectSummary.project;
      this.projectId = this.project.projectId;
      this.surveyId = this.project.survey.surveyId;
      this.projectCost = this.project.projectCost;
      this.projectDays = this.$formatter.daysFromDates(
        this.project.startDate,
        this.project.endDate
      );
      this.periodName = this.projectSummary.periodName;
      this.periods = this.projectSummary.projectPeriods;

      this.periodDate = new Date(this.projectSummary.periodName);
      this.byQuestionData = this.$helpers.mapDataPointToChartDataSet(
        this.projectSummary.responsesByQuestion
      );

      this.comments = this.projectSummary.comments
        .sort((a, b) => (a.answerIndex < b.answerIndex ? 1 : -1))
        .sort((a, b) => (a.questionText > b.questionText ? 1 : -1));
      //Call Another Function to get Users
      this.projectUsers = this.projectSummary.projectUsers;
      this.loadAllUsers();
      this.projectMetrics = this.projectSummary.projectMetrics;
      this.documents = this.projectSummary.documents;
      this.news = this.projectSummary.news;
      this.presetTags = this.project.presetTags;

      // this.contractorLogo = this.project.primaryContractor.organizationLogo;
      // this.ownerLogo = this.project.primaryOwner.organizationLogo;

      this.meetings = this.projectSummary.meetings;
      this.facilitatorSurveys = this.projectSummary.facilitatorSurveys;
      this.$store.dispatch('dataLoaded');
      this.$parent.showKickoffWizardIfHaveUsers();
    },
    loadAllUsers() {
      axios
        .get(
          `${this.$api_url}/projects/${this.projectId}/users?inactiveUsers=true&periodId=${this.periodName}`,
          this.getReqConfig()
        )
        .then((response) => {
          this.allProjectUsers = response.data;
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateByQuestion(update) {
      //Surveys by question
      this.periodName = update.period;
      let url = `${this.$api_url}/surveys/${this.surveyId}/data-by-question?period=${update.period}`;
      axios
        .get(url, this.getReqConfig())
        .then((response) => {
          this.byQuestionData = this.$helpers.mapDataPointToChartDataSet(
            response.data
          );
        })
        .catch((err) => {
          if (err.response && err.response.statusText) {
            this.errors.push({ message: err.response.statusText });
          } else {
            this.errors.push(err);
          }
        });

      //Respondent list
      url = `${this.$api_url}/projects/${this.projectId}/users?periodId=${update.period}&inactiveUsers=true`;
      axios
        .get(url, this.getReqConfig())
        .then((response) => {
          this.allProjectUsers = response.data;
        })
        .catch((err) => {
          if (err.response && err.response.statusText) {
            this.errors.push({ message: err.response.statusText });
          } else {
            this.errors.push(err);
          }
        });
      this.refreshComments(update);
    },
    refreshProjectItems(query) {
      if (query === undefined) {
        query = {
          name: "project-metrics",
        };
      }

      if (query.name === "project-metrics") {
        if (query.id === undefined) {
          let url = `${this.$api_url}/projects/${this.projectId}/project-metrics`;
          axios
            .get(url, this.getReqConfig())
            .then((response) => {
              this.projectMetrics = response.data;
            })
            .catch((err) => {
              if (err.response && err.response.statusText) {
                this.errors.push({ message: err.response.statusText });
              } else {
                this.errors.push(err);
              }
            });
        } else {
          let url = `${this.$api_url}/project-metrics/${query.id}`;
          axios
            .get(url, this.getReqConfig())
            .then((response) => {
              let index = this.projectMetrics.findIndex(
                (p) => p.projectMetricId === query.id
              );
              if (index !== -1) {
                this.projectMetrics.splice(index, 1, response.data);
              }
            })
            .catch((err) => {
              if (err.response && err.response.statusText) {
                this.errors.push({ message: err.response.statusText });
              } else {
                this.errors.push(err);
              }
            });
        }
      } else if (query.name === "news-links") {
        let url = `${this.$api_url}/projects/ ${this.projectId}}/news-links`;
        axios
          .get(url, this.getReqConfig())
          .then((response) => {
            this.news = response.data;
          })
          .catch((err) => {
            if (err.response && err.response.statusText) {
              this.errors.push({ message: err.response.statusText });
            } else {
              this.errors.push(err);
            }
          });
      }
    },
    refreshComments(update) {
      if (update.hasOwnProperty("period")) {
        let url = `${this.$api_url}/surveys/${this.surveyId}/responses/comments?period=${update.period}`;
        axios
          .get(url, this.getReqConfig())
          .then((response) => {
            this.comments = response.data;
          })
          .catch((err) => {
            if (err.response && err.response.statusText) {
              this.errors.push({ message: err.response.statusText });
            } else {
              this.errors.push(err);
            }
          });
      }
    },
    hideComment(index) {
      this.$set(this.comments, index, {
        ...this.comments[index],
        commentHidden: true,
      });
      let url = `${this.$api_url}/surveys/${this.surveyId}/responses/comments/${this.comments[index].id}/hidden/true`;
      axios
        .put(url, {}, this.getReqConfig())
        .then((res) => console.log(res))
        .catch((err) => console.log(err));
    },
    unhideComment(index) {
      this.$set(this.comments, index, {
        ...this.comments[index],
          commentHidden: false,
      });

      let url = `${this.$api_url}/surveys/${this.surveyId}/responses/comments/${this.comments[index].id}/hidden/false`;
      axios
        .put(url, {}, this.getReqConfig())
        .then((res) => console.log(res))
        .catch((err) => console.log(err));
    },
    refreshProjectUsers() {
      this.loadingUsers = true;
      axios
        .get(
          `${this.$api_url}/projects/${this.projectId}/users`,
          this.getReqConfig()
        )
        .then((response) => {
          this.projectUsers = response.data;
        })
        .catch((err) => {
          this.errors.push(err);
        })
        .finally(() => {
          this.loadingUsers = false;
        });
    },
    refreshAddUserData() {
      this.refreshProjectUsers();
      this.loadAllUsers();
      //Adding a user can also create an organization, so they need to be updated!
      this.loadAllOrganizations();
    },
    addMeeting() {
      this.$refs.ProjectLayout.addMeeting();
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
